import { differenceInMinutes, isAfter, isValid, parseISO } from 'date-fns';

/* ============================================================
   Time formatting
   ============================================================ */

/**
 * Format seconds as HH:MM:SS for the live timer display.
 * Examples:
 *   formatSeconds(45)     -> "00:00:45"
 *   formatSeconds(74)     -> "00:01:14"
 *   formatSeconds(4472)   -> "01:14:32"
 */
export function formatSeconds(secs) {
  const s = Math.max(0, Math.floor(Number(secs) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
}

/**
 * Format total minutes as "Xh Ym" for table cells and reports.
 * Examples:
 *   formatMinutes(0)    -> "0m"
 *   formatMinutes(45)   -> "45m"
 *   formatMinutes(60)   -> "1h"
 *   formatMinutes(74)   -> "1h 14m"
 *   formatMinutes(120)  -> "2h"
 */
export function formatMinutes(mins) {
  const total = Math.max(0, Math.floor(Number(mins) || 0));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/**
 * Convert seconds to minutes (used when reading timer values
 * stored as seconds and presenting them in tables as minutes).
 */
export function secondsToMinutes(secs) {
  return Math.floor((Number(secs) || 0) / 60);
}

/* ============================================================
   Standard time + variance
   ============================================================ */

/**
 * Detect whether a task description triggers the "meeting" preset
 * (BIT 7): keywords "meeting" or "zoom" -> std 60min, ±30 variance.
 * Otherwise default to std 30min, ±15 variance.
 *
 * Matches whole words, case-insensitive. Examples:
 *   "Client meeting" -> 60
 *   "Zoom call with vendor" -> 60
 *   "Update budget sheet" -> 30
 */
export function getStdTime(taskDescription = '') {
  const text = String(taskDescription).toLowerCase();
  const hasMeetingKeyword = /\b(meeting|zoom)\b/.test(text);
  return hasMeetingKeyword ? 60 : 30;
}

/** Get the allowed variance (± minutes) for a given task description */
export function getVariance(taskDescription = '') {
  return getStdTime(taskDescription) === 60 ? 30 : 15;
}

/**
 * Classify actual time against standard (BIT 14 — Late / On Time / Early).
 *  - actual < std - variance -> 'early'
 *  - actual > std + variance -> 'late'
 *  - otherwise               -> 'ontime'
 *
 * @param {number} actualMins  Total minutes logged on the task
 * @param {number} stdMins     Standard time for the task
 * @param {number} [variance]  ± variance window; defaults to half of std
 * @returns {'early'|'ontime'|'late'}
 */
export function calcVariance(actualMins, stdMins, variance) {
  const a = Math.max(0, Number(actualMins) || 0);
  const s = Math.max(0, Number(stdMins) || 0);
  // Default variance: ±15 for 30min std, ±30 for 60min std, else half of std
  const v = Number(variance);
  const variancePadding = Number.isFinite(v) && v >= 0
    ? v
    : (s === 60 ? 30 : s === 30 ? 15 : Math.floor(s / 2));

  if (a < s - variancePadding) return 'early';
  if (a > s + variancePadding) return 'late';
  return 'ontime';
}

/* ============================================================
   Overdue detection
   ============================================================ */

/**
 * A task is overdue iff closure date has passed AND it's not Done.
 * Per BIT 8: status is set to 'overdue' automatically by background job;
 * this helper is for client-side display fallback.
 *
 * @param {string|Date} closureDate
 * @param {string} status
 * @returns {boolean}
 */
export function isOverdue(closureDate, status) {
  if (!closureDate) return false;
  if (status === 'done') return false;
  if (status === 'overdue') return true;

  const d = closureDate instanceof Date ? closureDate : parseISO(closureDate);
  if (!isValid(d)) return false;

  // Compare against end-of-day on closureDate
  const endOfClosureDay = new Date(d);
  endOfClosureDay.setHours(23, 59, 59, 999);
  return isAfter(new Date(), endOfClosureDay);
}

/**
 * Minutes between two dates (positive = b later than a).
 * Useful for "Task open for X min" displays.
 */
export function minutesBetween(a, b) {
  const da = a instanceof Date ? a : parseISO(a);
  const db = b instanceof Date ? b : parseISO(b);
  if (!isValid(da) || !isValid(db)) return 0;
  return differenceInMinutes(db, da);
}

/* ============================================================
   Working-hours helper (BIT 14 — Reports)
   "Xh Xm of XXh available · 7hrs/day × working days"
   ============================================================ */
export const HOURS_PER_DAY = 7;

/**
 * Available minutes for a given number of working days.
 * Caller is responsible for computing the working-day count
 * for the chosen period (today / week / month / last month).
 */
export function availableMinutes(workingDays) {
  return Math.max(0, Math.floor(Number(workingDays) || 0)) * HOURS_PER_DAY * 60;
}